import { Fragment, useMemo } from 'react'
import styled from 'styled-components'
import { Label } from '@/components/text'
import { colors } from '@/styles'
import { Reveal } from '@/components/animations'
import { ChevronIcon, ToasterItemCompletedIcon, ToasterItemErrorIcon } from '@/components/icons'
import { useTask } from '@/models/toaster/hooks'
import { TaskProps, TaskState } from './types'
import ToasterStep from './toaster-step'

function makeKey<X extends string, Y extends string>(x: X, y: Y) {
  return `${x}.${y}` as `${X}.${Y}`
}

const ItemWrapper = styled.div`
  :not(:last-child) {
    border-bottom: 1px solid #e6e6e6;
  }
`

const ArrowWrapper = styled.div`
  transform: rotate(0deg);
  transition: transform 0.3s ease-in-out 0s;
  color: #000;
`

const TaskHeading = styled.button`
  padding: 0;
  width: 100%;
  background: unset;
  border: unset;
  box-shadow: unset;
  cursor: pointer;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  &[aria-expanded='true'] {
    ${ArrowWrapper} {
      transform: rotate(-180deg);
    }
  }
`

const TaskHeadingLeft = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 0 8px;
  color: ${colors.grey48};
`

const TaskTitle = styled(Label)<{ state?: TaskState }>`
  color: ${colors.grey08};
  display: flex;
  align-items: center;
  text-align: left;

  ${({ state }) =>
    state === 'complete' &&
    `
    text-decoration-line: line-through;
    text-decoration-style: solid;
    color: ${colors.grey56};
  `}

  ${({ state }) =>
    state === 'error' &&
    `
    color: ${colors.red40};
  `}
`

const TaskBody = styled(Label)`
  padding: 0 24px 16px 32px;
  line-height: 16px;
  color: ${colors.grey32};
`

const TaskDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px 0;
`

const TaskSteps = styled.div`
  padding: 10px 0 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const getTaskIcon = (state: TaskState, icon?: any) => {
  switch (state) {
    case 'incomplete': {
      if (icon) return icon
      return Fragment
    }
    case 'complete':
      return ToasterItemCompletedIcon
    case 'error':
    default:
      return ToasterItemErrorIcon
  }
}

const ToasterItem: React.FC<TaskProps> = ({
  id,
  title,
  icon,
  description,
  steps,
  complete = null,
  hideCompleted,
  isOpen,
  setOpen
}) => {
  const { state } = useTask(id)
  const handleHeadingClick = () => {
    if (state === 'error') return // tooltip explaining why
    setOpen(currentId => (currentId === id ? null : id))
  }
  const Icon = useMemo(() => getTaskIcon(state, icon), [state, icon])
  if (state === 'complete' && hideCompleted) return null
  return (
    <ItemWrapper>
      <TaskHeading onClick={handleHeadingClick} aria-expanded={isOpen}>
        <TaskHeadingLeft>
          {state && <Icon width={20} height={20} />}
          <TaskTitle state={state} fontSize="14px">
            {title}
          </TaskTitle>
        </TaskHeadingLeft>
        <ArrowWrapper>
          <ChevronIcon width={14} height={8} />
        </ArrowWrapper>
      </TaskHeading>
      <Reveal isOpen={isOpen} noTransform>
        <TaskBody fontSize="12px" type="small">
          <TaskDescription>{description}</TaskDescription>
          {steps.length > 0 && (
            <TaskSteps>
              {steps.map(({ key, ...rest }) => (
                <ToasterStep
                  key={`toaster-step-${key}`}
                  {...rest}
                  id={makeKey(id, key)}
                  only={steps.length === 1}
                />
              ))}
              {state === 'complete' && complete}
            </TaskSteps>
          )}
        </TaskBody>
      </Reveal>
    </ItemWrapper>
  )
}

export default ToasterItem
